import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  MagnifyingGlass, ArrowClockwise, Warning, Trash, CheckCircle,
} from '@phosphor-icons/react';

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
}
function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * DuplicateScannerModal — scans all card_vendors for duplicate card numbers.
 * Shows every duplicate group with per-entry vendor/date/user/status info.
 * Admin can delete a specific dupe entry inline (soft-delete, reverses debit).
 */
export default function DuplicateScannerModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [deletingId, setDeletingId] = useState('');

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor-cards/duplicates-scan');
      setData(r.data || null);
      if (!(r.data?.duplicate_groups || []).length) {
        toast.success('Koi duplicate nahi mila 🎉');
      } else {
        toast.warning(`${r.data.total_duplicate_card_numbers} card numbers duplicate hain (${r.data.total_duplicate_rows} total rows).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan fail');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) scan(); }, [open]);

  const handleDelete = async (entry, group) => {
    const confirmed = window.confirm(
      `Ye duplicate delete karna hai?\n\n` +
      `Card: ****${entry.card_number_last4}\n` +
      `Vendor: ${entry.vendor_name}\n` +
      `Limit: ${fmt(entry.card_limit)}\n` +
      `Uploaded: ${fmtDate(entry.uploaded_at)} by ${entry.uploaded_by_name}\n\n` +
      `Ledger reversal auto-post hoga. Sure?`
    );
    if (!confirmed) return;
    setDeletingId(entry.card_id);
    try {
      const r = await api.delete(`/vendor-cards/${entry.card_id}`);
      toast.success(`Deleted · ₹${(r.data?.reversed_amount || 0).toFixed(2)} credited back`);
      // Optimistically remove this entry from local state
      setData((prev) => {
        if (!prev) return prev;
        const groups = prev.duplicate_groups
          .map((g) => g.card_number === group.card_number
            ? { ...g, entries: g.entries.filter((e) => e.card_id !== entry.card_id), count: g.count - 1 }
            : g)
          .filter((g) => g.count > 1);
        return {
          ...prev,
          duplicate_groups: groups,
          total_duplicate_card_numbers: groups.length,
          total_duplicate_rows: groups.reduce((s, g) => s + g.count, 0),
        };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete fail');
    } finally { setDeletingId(''); }
  };

  const groups = data?.duplicate_groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="duplicate-scanner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagnifyingGlass size={18} weight="duotone" className="text-fuchsia-600" />
            Duplicate Card Scanner
          </DialogTitle>
          <DialogDescription className="text-xs">
            All card vendors ke across scan karke duplicate card numbers dhoondho — same card multiple jagah entered hai to yahaan sab dikhega.
          </DialogDescription>
        </DialogHeader>

        {/* Summary bar */}
        <div className="flex items-center gap-3 flex-wrap bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3">
          <div>
            <div className="text-[10px] uppercase text-fuchsia-700 font-semibold">Duplicate Card Numbers</div>
            <div className="text-2xl font-bold text-fuchsia-900" data-testid="dupe-total-groups">
              {data?.total_duplicate_card_numbers ?? '—'}
            </div>
          </div>
          <div className="border-l border-fuchsia-300 h-10" />
          <div>
            <div className="text-[10px] uppercase text-fuchsia-700 font-semibold">Total Duplicate Rows</div>
            <div className="text-2xl font-bold text-fuchsia-900" data-testid="dupe-total-rows">
              {data?.total_duplicate_rows ?? '—'}
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button
              onClick={scan}
              disabled={loading}
              variant="outline"
              className="gap-1"
              data-testid="dupe-scan-rescan-btn"
            >
              <ArrowClockwise size={14} /> {loading ? 'Scanning…' : 'Re-scan'}
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading && !data ? (
          <div className="p-8 text-center text-sm text-gray-500">Scanning DB…</div>
        ) : groups.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
            <div className="text-sm font-semibold text-emerald-800">Zero duplicates 🎉</div>
            <div className="text-xs text-gray-500 mt-1">Har card unique hai across all vendors.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <div key={g.card_number} className="border border-red-200 rounded-lg overflow-hidden" data-testid={`dupe-group-${g.card_number_last4}`}>
                <div className="bg-red-50 px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Warning size={16} weight="duotone" className="text-red-600" />
                    <span className="font-mono font-bold text-red-900">
                      **** **** **** {g.card_number_last4}
                    </span>
                    <span className="text-[10px] bg-red-600 text-white font-bold rounded px-2 py-0.5">
                      {g.count} copies
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Vendor</th>
                        <th className="px-2 py-1.5 text-left">Holder</th>
                        <th className="px-2 py-1.5 text-right">Limit</th>
                        <th className="px-2 py-1.5 text-left">Uploaded</th>
                        <th className="px-2 py-1.5 text-left">By</th>
                        <th className="px-2 py-1.5 text-center">Status</th>
                        <th className="px-2 py-1.5 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.entries.map((e, i) => (
                        <tr key={e.card_id} className={`border-t ${i === 0 ? 'bg-emerald-50/50' : 'bg-white'}`}>
                          <td className="px-2 py-1.5">
                            <div className="font-semibold text-gray-900">{e.vendor_name}</div>
                            {i === 0 && <div className="text-[9px] text-emerald-700 font-semibold">ORIGINAL (oldest)</div>}
                          </td>
                          <td className="px-2 py-1.5 text-gray-700">{e.card_holder || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-semibold">{fmt(e.card_limit)}</td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{fmtDate(e.uploaded_at)}</td>
                          <td className="px-2 py-1.5 text-gray-600">{e.uploaded_by_name || '—'}</td>
                          <td className="px-2 py-1.5 text-center">
                            {e.is_used ? (
                              <span className="text-[10px] font-semibold bg-orange-100 text-orange-800 rounded px-1.5 py-0.5">
                                USED {e.used_by_name && `· ${e.used_by_name}`}
                              </span>
                            ) : (
                              <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">
                                UNUSED
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <button
                              onClick={() => handleDelete(e, g)}
                              disabled={deletingId === e.card_id || e.is_used}
                              className="p-1.5 rounded text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={e.is_used ? 'Used card cannot be deleted' : 'Delete this copy & reverse debit'}
                              data-testid={`dupe-delete-${e.card_id}`}
                            >
                              <Trash size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
